import { Injectable } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { forkJoin, map, Observable, switchMap } from 'rxjs';

import { StudentsService } from '../../core/services/students.service';
import { EnrollmentService } from '../../core/services/enrollment.service';
import { UtilityService } from '../../core/services/utility.service';
import { SessionStorageService } from '../../core/services/session-storage.service';

import { Student } from '../../core/models/student.model';
import { SchoolClass } from '../../core/models/school-class.model';
import { SchoolAdminTeacherFilterService } from '../../shared/components/school-admin-teacher-filter/school-admin-teacher-filter.service';

interface StudentResolverData {
  students: Student[];
  grades: string[];
  classes: SchoolClass[];
  licenseCount: number;
  myCoTeacherData: Student[];
}

@Injectable({
  providedIn: 'root'
})
export class StudentsResolver  {
  constructor(
    private studentService: StudentsService,
    private enrollService: EnrollmentService,
    private utilityService: UtilityService,
    private sessionStorageService: SessionStorageService,
    private teacherFilterService: SchoolAdminTeacherFilterService,
  ) { }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<StudentResolverData> {
    const currentUser = this.sessionStorageService.userData();

    // Fetch student list for entire school if we are a school admin, otherwise fetch for the current user
    const studentListScope = currentUser?.isSchoolAdmin() ? 'school' : 'mine';

    return this.teacherFilterService.initSchoolAdminTeacherFilter({allowAllOption: true})
      .pipe(
        switchMap(
          () => forkJoin([
            this.studentService.getStudents(studentListScope),
            this.enrollService.getRemainingInterventionLicensesCount(null, null),
            this.studentService.getCoTeacherAccessData(),
          ])
        )
      )
      .pipe(
        map((results) => {
          let students: Student[] = results[0] ;
          let licenseCount: number = results[1] ;
          let myCoTeacherData: Student[] = results[2];
          let gradesSet: Set<string> = new Set() ;
          let grades: string[] = [] ;
          let classes: SchoolClass[] = [] ;

          const finalStudentList = [
            ...students,
            ...myCoTeacherData.map(ctaStudent => ({
              ...ctaStudent,
              fromCoTeacherAccess: true,
            })),
          ].sort((a, b) =>
            // sort the array by last name, then first name
            a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
          );

          students = finalStudentList;

          // Add in all our current grade options
          students.forEach((student: Student) => {
            gradesSet.add(this.utilityService.getStudentGrade(student.grade)) ;
          }) ;

          // Return sorted grades
          grades = [ 'All' ].concat(this.utilityService.sortGrades(Array.from(gradesSet))) ;

          // Return sorted school classes
          classes = [ {
            name: 'All',
            schoolClassID: 0,
            teacherID: 0,
            createDate: (new Date()),
            status: ''
          }].concat(this.utilityService.sortClasses(this.utilityService.getUniqueSchoolClasses(students))) ;

          return {
            students: students,
            grades: grades,
            classes: classes,
            licenseCount: licenseCount,
            myCoTeacherData: myCoTeacherData,
          } ;
        })
      ) ;
  }
}
